import { useState, useEffect, useCallback } from 'react';
import { reservationsService } from '../lib/database';
import { getDataSource } from '../lib/config';
import type { Reservation } from '../types';

interface UseReservationsReturn {
  reservations: Reservation[];
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  create: (reservation: Omit<Reservation, 'id' | 'createdAt' | 'updatedAt'>) => Promise<Reservation>;
  update: (id: number, updates: Partial<Reservation>) => Promise<Reservation>;
  remove: (id: number) => Promise<void>;
  getByDate: (date: string) => Promise<Reservation[]>;
  getByTableId: (tableId: number) => Promise<Reservation[]>;
}

export const useReservations = (enabled = true): UseReservationsReturn => {
  const [reservations, setReservations] = useState<Reservation[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchReservations = useCallback(async () => {
    const dataSource = getDataSource();
    
    if (dataSource === 'mock') {
      // For mock data, we'll use empty array (no mock reservations yet)
      setReservations([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const data = await reservationsService.getAll();
      setReservations(data);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load reservations';
      setError(errorMessage);
      console.error('Error fetching reservations:', err);
      setReservations([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!enabled) return;
    fetchReservations();
  }, [fetchReservations, enabled]);

  const create = useCallback(async (reservation: Omit<Reservation, 'id' | 'createdAt' | 'updatedAt'>): Promise<Reservation> => {
    const dataSource = getDataSource();
    
    if (dataSource === 'mock') {
      const newReservation: Reservation = {
        ...reservation,
        id: Math.max(...reservations.map(r => r.id), 0) + 1,
        createdAt: Date.now(),
        updatedAt: Date.now(),
      };
      setReservations(prev => [...prev, newReservation]);
      return newReservation;
    }

    setError(null);
    try {
      const newReservation = await reservationsService.create(reservation);
      setReservations(prev => [...prev, newReservation]);
      return newReservation;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to create reservation';
      setError(errorMessage);
      throw err;
    }
  }, [reservations]);

  const update = useCallback(async (id: number, updates: Partial<Reservation>): Promise<Reservation> => {
    const dataSource = getDataSource();
    
    if (dataSource === 'mock') {
      const updatedReservation = reservations.find(r => r.id === id);
      if (!updatedReservation) throw new Error('Reservation not found');
      const newReservation = { ...updatedReservation, ...updates, updatedAt: Date.now() };
      setReservations(prev => prev.map(r => r.id === id ? newReservation : r));
      return newReservation;
    }

    setError(null);
    try {
      const updatedReservation = await reservationsService.update(id, updates);
      setReservations(prev => prev.map(r => r.id === id ? updatedReservation : r));
      return updatedReservation;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update reservation';
      setError(errorMessage);
      throw err;
    }
  }, [reservations]);

  const remove = useCallback(async (id: number): Promise<void> => {
    const dataSource = getDataSource();
    
    if (dataSource === 'mock') {
      setReservations(prev => prev.filter(r => r.id !== id));
      return;
    }

    setError(null);
    try {
      await reservationsService.delete(id);
      setReservations(prev => prev.filter(r => r.id !== id));
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to delete reservation';
      setError(errorMessage);
      throw err;
    }
  }, []);

  const getByDate = useCallback(async (date: string): Promise<Reservation[]> => {
    const dataSource = getDataSource();
    
    if (dataSource === 'mock') {
      return reservations.filter(r => r.reservationDate === date);
    }

    try {
      return await reservationsService.getByDate(date);
    } catch (err) {
      console.error('Error fetching reservations by date:', err);
      return [];
    }
  }, [reservations]);

  const getByTableId = useCallback(async (tableId: number): Promise<Reservation[]> => {
    const dataSource = getDataSource();
    
    if (dataSource === 'mock') {
      return reservations.filter(r => r.tableId === tableId);
    }

    try {
      return await reservationsService.getByTableId(tableId);
    } catch (err) {
      console.error('Error fetching reservations by table:', err);
      return [];
    }
  }, [reservations]);

  return {
    reservations,
    loading,
    error,
    refresh: fetchReservations,
    create,
    update,
    remove,
    getByDate,
    getByTableId,
  };
};

